import { datadogRum } from '@datadog/browser-rum'

import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import { DD_RUM_ENABLED_FLAG } from '@src/config/env'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import type { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { GenderName, getCurrentPageGenderContext } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import type {
  ISvdImage,
  SvdNativeCardVariant,
  SvdNativeProduct,
  SvdPromoConfig,
  SvdSocialProofData,
} from '@src/types/svd-product-card.types'
import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

/**
 * Raw SAND `displayConfig.displayType` wire values (README §8). The swatch renders per mode: a hex
 * chip, an image swatch, or a text chip.
 */
export const SVD_SWATCH_DISPLAY_TYPE = {
  HEX_COLOR: 'HEX_COLOR',
  IMAGE: 'IMAGE',
  TEXT: 'TEXT',
} as const

// A sold-out variant suppresses promo pills (parity with the legacy `getVariantTags`).
const SOLD_OUT_TAG_VALUE = 'sold out'

/**
 * Picks the initial selected variant for an SVD card. Variant-id keyed (not color) — falls back to
 * the first variant. Mirrors the legacy `getInitialSelectedVariant` but on the native SVD type.
 */
export function getInitialSvdVariant(
  cardVariants: SvdNativeCardVariant[],
  selectedVariantId?: number,
): SvdNativeCardVariant | undefined {
  if (!selectedVariantId) return cardVariants[0]
  return cardVariants.find(variant => variant.variantId === selectedVariantId) ?? cardVariants[0]
}

/**
 * Whether the card should render a swatch row: only when the product exposes a real anchor
 * dimension (`displayConfig` non-null) on more than one variant. No-VDA products (null config,
 * single variant) render without swatches.
 */
export function shouldRenderSvdSwatches(product: SvdNativeProduct): boolean {
  const swatchable = product.cardVariants.filter(variant => variant.displayConfig !== null)
  return swatchable.length > 1
}

/**
 * Swatchable variants (real `displayConfig`) capped at `limit`, plus the count hidden beyond the cap.
 * Mirrors the legacy card's `optionLimit` + "+N" overflow so a many-variant tile doesn't overflow.
 */
export function getSvdSwatchVariants(
  cardVariants: SvdNativeCardVariant[],
  limit: number,
): { visible: SvdNativeCardVariant[]; overflowCount: number } {
  const swatchable = cardVariants.filter(variant => variant.displayConfig !== null)
  return { visible: swatchable.slice(0, limit), overflowCount: Math.max(0, swatchable.length - limit) }
}

/**
 * The currently-active promo on a variant, or null. Mirrors the legacy `getValidPromo`: keeps only
 * offers whose `[startTime, endTime]` (UTC-naive strings) brackets now, then dedupes their `tags`
 * and concatenates their offer messages. Used to surface promo pills (Y1); pricing fields are left
 * for the deferred pricing work.
 */
export function getSvdActivePromo(promoConfig: SvdPromoConfig[]): { tags: string[]; promoOfferMessage: string } | null {
  if (promoConfig.length === 0) return null

  const now = Date.now()
  const active = promoConfig.filter(promo => {
    const start = Date.parse(promo.startTime + 'Z')
    const end = Date.parse(promo.endTime + 'Z')
    return start <= now && end >= now
  })
  if (active.length === 0) return null

  const seen = new Set<string>()
  const tags: string[] = []
  let promoOfferMessage = ''
  for (const promo of active) {
    promoOfferMessage += promo.promoOfferMessage
    for (const tag of promo.tags) {
      if (!seen.has(tag)) {
        seen.add(tag)
        tags.push(tag)
      }
    }
  }
  return { tags, promoOfferMessage }
}

/**
 * Tags to render over the card image for the selected variant. Backend variant tags by default;
 * an active promo's pills replace them (Y1) unless the variant is sold out — sold-out always wins.
 * Mirrors the legacy `getVariantTags` precedence on the native SVD shape.
 */
export function getSvdApplicableTags(variant: SvdNativeCardVariant | undefined): ProductCardTag[] {
  if (!variant) return []

  const variantTags: ProductCardTag[] = variant.tags.map(tag => ({
    value: tag.value,
    displayName: tag.displayName,
    tagType: tag.tagType as ProductCardTag['tagType'],
  }))

  const isSoldOut = variantTags.some(tag => tag.value.toLowerCase() === SOLD_OUT_TAG_VALUE)
  const activePromo = getSvdActivePromo(variant.promoConfig)
  if (!isSoldOut && activePromo && activePromo.tags.length > 0) {
    return activePromo.tags.map(value => ({ value, tagType: 'PRIMARY' as ProductCardTag['tagType'] }))
  }
  return variantTags
}

/**
 * Human-facing social-proof labels for a variant (Y2): stock urgency + recent-sales, in that order.
 * Uses only the backend `*DisplayName` copy (no synthesized strings, no new TMS keys); empty when
 * the wire carries no social proof — which is every product today.
 */
export function getSvdSocialProofLabels(socialProof: SvdSocialProofData | null | undefined): string[] {
  if (!socialProof) return []
  return [socialProof.stockTagDisplayName, socialProof.soldRecentlyTagDisplayName].filter((label): label is string =>
    Boolean(label),
  )
}

/**
 * Filters a variant's images to the current page's gender context — parity with the legacy/DS card's
 * `getImagesForOptions` (`product-card.helpers.ts`), which drops images whose `description` is
 * `'men'`/`'women'` when it doesn't match. `ISvdImage.description` carries the same marker; any other
 * (or no) description always passes through untouched. `gender` lets a section override explicitly;
 * omitted, this derives it from the current URL the same way legacy's default does — so the common
 * case needs no section wiring at all.
 */
export function getSvdImagesForGender(images: ISvdImage[], gender?: GenderName): ISvdImage[] {
  // Resolved once — depends only on `gender` + the current URL, never on the image being tested — not
  // per image inside the filter callback.
  const currentPageGenderContext =
    gender ?? (isBrowser() ? getCurrentPageGenderContext(window.location.pathname, window.location.search) : undefined)

  return images.filter(image => {
    if (image.description !== GenderName.MALE && image.description !== GenderName.FEMALE) return true
    return !currentPageGenderContext || currentPageGenderContext === image.description
  })
}

/**
 * DD RUM parity for an empty image set — mirrors the legacy card's `reportEmptyProductImages`
 * (`product-card.helpers.ts`), reading SVD-native variant fields instead of `CardVariant` (Option 2:
 * no legacy type coupling). Fires when gender filtering (or the wire itself) leaves a variant with
 * zero images, so a blank tile is diagnosable in DD RUM instead of silently invisible.
 */
export function reportSvdEmptyProductImages(
  selectedVariant: SvdNativeCardVariant | undefined,
  gender: GenderName | undefined,
  deviceType: DEVICE_TYPES | undefined,
): void {
  if (!isBrowser()) return

  try {
    const productId = selectedVariant?.variantId
    const colorName = selectedVariant?.displayConfig?.value
    // eslint-disable-next-line i18next/no-literal-string -- DD RUM debug string, not user-facing copy
    const errorMessage = `SVD product card images array is empty for variant ${productId} color ${colorName ?? 'unknown'}`

    const debugData = {
      variant: selectedVariant
        ? {
            variantId: selectedVariant.variantId,
            title: selectedVariant.title,
            vendorType: selectedVariant.vendorType,
            url: selectedVariant.url,
            svids: selectedVariant.svids,
            displayConfig: selectedVariant.displayConfig,
            price: selectedVariant.price,
            tags: selectedVariant.tags,
          }
        : null,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pathname: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      deviceType,
      gender,
      sessionId: getSessionStorage().getItem<string>('sessionId', { isGlobal: true }),
      isServerSide: false,
    }

    if (DD_RUM_ENABLED_FLAG) {
      try {
        datadogRum.addError(new Error(errorMessage), {
          tags: {
            type: 'EMPTY_PRODUCT_IMAGES',
            module: MODULES.PRODUCT_CARD,
            feature: FEATURES.IMAGE,
            /* eslint-disable i18next/no-literal-string -- DD RUM debug tags, not user-facing copy */
            productId: productId ?? 'unknown',
            colorName: colorName ?? 'unknown',
            variantTitle: selectedVariant?.title ?? 'unknown',
            vendorType: selectedVariant?.vendorType ?? 'unknown',
            /* eslint-enable i18next/no-literal-string */
          },
          context: { debugInfo: debugData },
        })
      } catch (rumError) {
        // eslint-disable-next-line no-console -- surface RUM failures without crashing the card
        console.error('Failed to report to DataDog RUM:', rumError)
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console -- best-effort telemetry, never break rendering
    console.error('Failed to report empty SVD product images:', error)
  }
}
